import React, {
    createContext,
    use,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
    type PropsWithChildren,
} from "react";
import { type GQL } from "@binale-tech/shared";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { CompanyContext } from "@domain/context/CompanyContext";
import { calendarItemStatesQuery } from "@domain/context/queries/calendarEventsQueries.graphql";
import { calendarItemStateSet as calendarItemStateSetMutation } from "@domain/context/mutations/calendarEventMutations.graphql";
import { useToastSaver } from "@ui-components/hooks/useToastSaver";
import { stateMapKey } from "@accounting/common/calendar/calendarItemState";

// SP3 of the calendar feature (FIBU-22). A SEPARATE provider from SP1's
// CalendarEventsProvider (design spec §III.11 decision #5, §14.6) - the state
// fetch runs ONCE per company (no date-window dependency), unlike the events
// fetch which re-runs per visible window/year; folding them together would
// blur the combined `loading` flag and force an unneeded state re-fetch on
// every year change. Mounted in the GLOBAL ContextProvider tree next to
// CalendarEventsProvider (src/domain/context/index.tsx), same reasoning as
// SP1 §14.5. Apollo runs `no-cache` everywhere in this codebase, so state
// here is plain local `useState`, patched from mutation responses.

interface ICalendarItemStateContext {
    stateByKey: ReadonlyMap<string, GQL.ICalendarItemState>;
    loading: boolean;
}

export type CalendarItemStateSetInput = Omit<GQL.ICalendarItemStateInput, "companyId">;

interface ICalendarItemStateController {
    setItemState: (input: CalendarItemStateSetInput) => Promise<GQL.ICalendarItemState | undefined>;
}

export const CalendarItemStateContext = createContext<ICalendarItemStateContext>({
    stateByKey: new Map(),
    loading: false,
});
export const CalendarItemStateControlContext = createContext<ICalendarItemStateController>({
    setItemState: () => Promise.reject(),
});

export const CalendarItemStateProvider: FC<PropsWithChildren> = ({ children }) => {
    const client = useApolloClient();
    const { companyGQL } = use(CompanyContext);
    const toastSaver = useToastSaver();
    const companyId = companyGQL?.id;

    const [states, setStates] = useState<GQL.ICalendarItemState[]>([]);
    const [loading, setLoading] = useState(false);

    // Review fix (FIBU-22 final pass): same stale-response guard as
    // CalendarEventsContext - a slower in-flight request from the PREVIOUS company
    // must not clobber fresher state once the company has since changed.
    const epochRef = useRef(0);

    const [setMutation] = useMutation(calendarItemStateSetMutation);

    const fetchStates = useCallback(async () => {
        const epoch = ++epochRef.current;
        if (!companyId) {
            setStates([]);
            return;
        }
        setLoading(true);
        try {
            const res = await client.query({
                query: calendarItemStatesQuery,
                fetchPolicy: "no-cache",
                variables: { companyId },
            });
            if (epochRef.current !== epoch) {
                return;
            }
            setStates(res.data.calendarItemStates);
        } catch {
            if (epochRef.current === epoch) {
                setStates([]);
            }
        } finally {
            if (epochRef.current === epoch) {
                setLoading(false);
            }
        }
    }, [client, companyId]);

    useEffect(() => {
        fetchStates();
    }, [fetchStates]);

    const setItemState = useCallback(
        async (input: CalendarItemStateSetInput) => {
            if (!companyId) {
                return undefined;
            }
            const saved = await toastSaver(() =>
                setMutation({ variables: { input: { ...input, companyId } } }).then(
                    (res) => res.data?.calendarItemStateSet,
                ),
            );
            if (saved) {
                setStates((prev) => {
                    const idx = prev.findIndex((s) => s.refType === saved.refType && s.refId === saved.refId);
                    if (idx === -1) {
                        return [...prev, saved];
                    }
                    const next = [...prev];
                    next[idx] = saved;
                    return next;
                });
            }
            return saved;
        },
        [companyId, setMutation, toastSaver],
    );

    const stateByKey = useMemo(
        () => new Map(states.map((s): [string, GQL.ICalendarItemState] => [stateMapKey(s.refType, s.refId), s])),
        [states],
    );

    const controller: ICalendarItemStateController = useMemo(() => ({ setItemState }), [setItemState]);
    const value: ICalendarItemStateContext = useMemo(() => ({ stateByKey, loading }), [stateByKey, loading]);

    return (
        <CalendarItemStateContext value={value}>
            <CalendarItemStateControlContext value={controller}>{children}</CalendarItemStateControlContext>
        </CalendarItemStateContext>
    );
};
